import React from 'react';

import { Collapse } from '@mui/material';
import { IconButton } from '@infoods/lifebase-ui';
import ArrowDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { useToggle } from 'react-use';
import { observer } from 'mobx-react';

import classes from './LeaderboardRowMobile.module.scss';
import { LeaderboardRowInfo } from '../../../../../interfaces/leaderboard/LeaderboardRowInfo';
import { GridTableCell } from '../../../../../../shared/components/table-components';
import {
  LeaderboardRowMobileWrapper,
  LeaderboardRowMobileVariant,
} from './LeaderboardRowMobileWrapper/LeaderboardRowMobileWrapper';
import { RankCell } from '../RankCell/RankCell';
import { InitialScanIndicator } from '../InitialScanIndicatorCell/InitialScanIndicator';
import { CustomerCell } from '../CustomerCell/CustomerCell';
import {
  LEADERBOARD_COLUMN,
  LeaderboardColumn,
  RenderColumnInfo,
} from '../../../ChallengeLeaderboard.settings';
import { InfoItem } from './InfoItem/InfoItem';
import { MetricTrendValue } from '../MetricTrendValue/MetricTrendValue';
import { leaderboardStore } from '../../../../../stores/leaderboardStore';
import { getValueOrDash } from '../../../../../../shared/utils/stringUtils/getValueOrDash/getValueOrDash';
import { MuscleMultiplierAdditionalInfo } from '../MuscleMultiplierAdditionalInfo/MuscleMultiplierAdditionalInfo';
import { getFormattedDate } from '../../../../../../shared/utils/dateUtils';
import {
  dateTimeFormat,
  localDateTimeFormat,
} from '../../../../../../shared/constants/apiDateFormat';

type LeaderboardRowMobileProps = {
  leaderboardRow: LeaderboardRowInfo;
  leaderboardColumns: LeaderboardColumn[];
  variant?: LeaderboardRowMobileVariant;
};

const LeaderboardRowMobile = observer(
  ({
    leaderboardRow,
    leaderboardColumns,
    variant = 'surface',
  }: LeaderboardRowMobileProps) => {
    const [expanded, toggleExpanded] = useToggle(false);
    const { challengeInfo } = leaderboardStore;
    const { scoringRules } = challengeInfo || {};

    const renderColumnsInfo: RenderColumnInfo = {
      [LEADERBOARD_COLUMN.RANK]: null,
      [LEADERBOARD_COLUMN.INITIAL_SCAN_INDICATOR]: leaderboardRow.initialScanDate ? (
        <InfoItem
          title="Initial Scan Completed:"
          description={getFormattedDate(
            leaderboardRow.initialScanDate,
            localDateTimeFormat,
            dateTimeFormat,
          )}
          indicator={<InitialScanIndicator isCompleted={true} hide={!expanded} />}
        />
      ) : (
        <InfoItem
          title="Initial Scan Not Completed"
          indicator={<InitialScanIndicator isCompleted={false} hide={!expanded} />}
        />
      ),
      [LEADERBOARD_COLUMN.CUSTOMER]: null,
      [LEADERBOARD_COLUMN.TOTAL_SCORE]: (
        <InfoItem title="Total Score" value={getValueOrDash(leaderboardRow.totalScore)} />
      ),
      [LEADERBOARD_COLUMN.FOOD_TRACKING_PERCENT]: (
        <InfoItem
          title="Food Tracking"
          description="%"
          value={getValueOrDash(leaderboardRow.foodTrackingPercentage, '%')}
        />
      ),
      [LEADERBOARD_COLUMN.FOOD_TRACKING_RANK]: (
        <InfoItem
          title="Food Tracking"
          description="rank"
          value={getValueOrDash(leaderboardRow.foodTrackingRank)}
        />
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_PERCENT]: (
        <InfoItem
          title="Fat Loss"
          description="%"
          value={
            <MetricTrendValue
              value={leaderboardRow.fatLossPercentage}
              arrowOnPositive="down"
              unit="%"
            />
          }
        />
      ),
      [LEADERBOARD_COLUMN.ADJUSTED_FAT_LOSS_PERCENT]: (
        <InfoItem
          title="Adjusted Fat Loss"
          description="%"
          value={
            <MetricTrendValue
              value={leaderboardRow.adjustedFatLossPercentage}
              arrowOnPositive="down"
              unit="%"
            />
          }
        />
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_RANK]: (
        <InfoItem
          title="Fat Loss"
          description="rank"
          value={getValueOrDash(leaderboardRow.fatLossRank)}
        />
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_LBS]: (
        <InfoItem
          title="Fat Loss"
          description="lbs"
          value={
            <MetricTrendValue value={leaderboardRow.fatLossLbs} arrowOnPositive="down" />
          }
        />
      ),
      [LEADERBOARD_COLUMN.MUSCLE_GAIN_LBS]: (
        <InfoItem
          title="Muscle Gain"
          description="lbs"
          value={
            <div>
              <MetricTrendValue
                value={leaderboardRow.muscleGainLbs}
                arrowOnPositive="up"
              />
              &nbsp;
              <MuscleMultiplierAdditionalInfo
                scoringRuleType={scoringRules}
                muscleGainLbs={leaderboardRow.muscleGainLbs}
                muscleGainMultiplier={leaderboardRow.muscleGainMultiplier}
              />
            </div>
          }
        />
      ),
      [LEADERBOARD_COLUMN.MUSCLE_MULTIPLIER]: (
        <InfoItem
          title="Muscle Multiplier"
          value={
            <>{getValueOrDash(leaderboardRow.muscleGainMultiplier, '*', 'prefix')}</>
          }
        />
      ),
      [LEADERBOARD_COLUMN.ADJUSTED_SCORE_PERCENT]: (
        <InfoItem
          title="Adjusted Score"
          description="%"
          value={
            <MetricTrendValue
              value={leaderboardRow.adjustedFatLossPercentage}
              arrowOnPositive="down"
              unit="%"
            />
          }
        />
      ),
    };

    return (
      <LeaderboardRowMobileWrapper variant={variant}>
        <RankCell rank={leaderboardRow.rank} />
        <GridTableCell justify="center" align="center">
          <InitialScanIndicator
            hide={expanded}
            isCompleted={Boolean(leaderboardRow.initialScanDate)}
          />
        </GridTableCell>
        <CustomerCell
          nickname={leaderboardRow.nickname}
          gender={leaderboardRow.gender}
          age={leaderboardRow.age}
        />
        <GridTableCell justify="end">
          <IconButton onClick={toggleExpanded}>
            {expanded ? (
              <ArrowUpRoundedIcon color="primary" />
            ) : (
              <ArrowDownRoundedIcon color="tertiary" />
            )}
          </IconButton>
        </GridTableCell>
        <Collapse className={classes.collapsibleContent} in={expanded}>
          {leaderboardColumns.map((column) => (
            <React.Fragment key={column}>{renderColumnsInfo[column]}</React.Fragment>
          ))}
        </Collapse>
      </LeaderboardRowMobileWrapper>
    );
  },
);

export { LeaderboardRowMobile };
